import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import SearchIcon from "../../../assets/icons/search-light-bg.svg";
import SearchScriptsView from "./SearchScriptView/SearchScriptsView";
import { useNavigate } from "react-router-dom";
import { useRef } from "react";
import { useListPaging } from "../../../hooks/useListPaging";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  selectScriptCategoriesByCreator,
  selectScriptLoading,
  selectScriptsByCreatorAndFilter,
  selectScriptsLoadingByCreatorAndFilter,
} from "../../../redux/chatScript/selectors/scriptSelectors";
import { fetchScriptCategoriesByCreator, fetchScriptsByFilter } from "../../../redux/chatScript/thunks/scriptThunks";
import ScriptList, { ScriptItem } from "../../../components/chatScripts/ScriptList/ScriptList";
import { REQUEST_LOADING_STATES } from "../../../helpers/constant";
import useFetchUserInfos from "../../../hooks/userHooks/useFetchUserInfos";
import CreateActionTile from "../../../components/CreateActionTile/CreateActionTile";
import Loader from "../../../components/loader/Loader";
import ScriptCategories from "../../../components/chatScripts/ScriptCategories/ScriptCategories";
import AppHeader from "../../../components/AppHeader/AppHeader";
import "./ChatScriptsView.less";

const ChatScriptsView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentUser: currentCreator } = useFetchUserInfos();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const categories = useAppSelector(selectScriptCategoriesByCreator(currentCreator?.id!));
  const loading = useAppSelector(selectScriptLoading);
  const scriptsEntry = useAppSelector(selectScriptsByCreatorAndFilter(currentCreator?.id!, activeCategory));
  const scriptsLoading = useAppSelector(selectScriptsLoadingByCreatorAndFilter(currentCreator?.id!, activeCategory));
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const lastRequestedPageRef = useRef<number>(0);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (currentCreator && categories.length === 0) {
      dispatch(fetchScriptCategoriesByCreator(currentCreator.id!));
    }
  }, [dispatch, currentCreator, categories.length]);

  const hasCategories = categories.length > 0;
  const items: ScriptItem[] = useMemo(
    () =>
      (scriptsEntry.items || []).map((s) => ({
        id: s._id,
        title: s.title,
        message: s.message,
        price: s.price ?? 0,
        type: "script",
        category: activeCategory,
      })),
    [scriptsEntry.items, activeCategory]
  );

  const [isSearching, setIsSearching] = useState(false);
  const showSearchIcon = !isSearching && hasCategories && items.length > 0;

  useEffect(() => {
    if (currentCreator && categories.length === 0 && loading === REQUEST_LOADING_STATES.IDLE) {
      dispatch(fetchScriptCategoriesByCreator(currentCreator.id!));
    }
  }, [dispatch, currentCreator, categories.length, loading]);

  useEffect(() => {
    if (currentCreator) {
      lastRequestedPageRef.current = scriptsEntry.page || 0;
      if (!scriptsEntry || !scriptsEntry.items || scriptsEntry.items.length === 0) {
        lastRequestedPageRef.current = 1;
        dispatch(fetchScriptsByFilter({ creatorId: currentCreator.id!, filterOrCategoryId: activeCategory, page: 1 }));
      }
    }
  }, [dispatch, currentCreator, activeCategory]);

  useListPaging({
    listRef,
    sentinelRef,
    itemsLength: scriptsEntry.items?.length || 0,
    page: scriptsEntry.page || 1,
    totalPages: scriptsEntry.totalPages || 0,
    pageSize: (scriptsEntry as any)?.pageSize || 15,
    loadingState: scriptsLoading,
    lastRequestedPageRef,
    canRun: !!currentCreator,
    onLoadNext: (nextPage) => {
      if (!currentCreator) return;
      dispatch(fetchScriptsByFilter({ creatorId: currentCreator.id!, filterOrCategoryId: activeCategory, page: nextPage }));
    },
  });

  return (
    <div className={`chat-scripts-view ${isSearching ? "searching" : ""}`.trim()}>
      {!isSearching && (
        <AppHeader
          title={t("scripts")}
          backButtonDestination="/common/setting"
          rightIconSrc={showSearchIcon ? SearchIcon : undefined}
          rightIconAlt="Search"
          onRightIconClick={() => setIsSearching(true)}
        />
      )}
      <main className="chat-scripts-content">
        {isSearching ? (
          <SearchScriptsView allItems={items} onCancel={() => setIsSearching(false)} />
        ) : (
          <>
            <CreateActionTile labelKey="create-new-script-text" onClick={() => navigate("/creator/chat-scripts/create")} />
            {loading === REQUEST_LOADING_STATES.PENDING || (loading === REQUEST_LOADING_STATES.IDLE && !hasCategories) ? (
              <div className="loader-wrapper">
                <Loader />
              </div>
            ) : hasCategories ? (
              <section className="scripts-panel">
                <ScriptCategories
                  creatorId={currentCreator?.id!}
                  categories={categories.map((c) => ({ _id: c._id, name: c.name }))}
                  activeCategory={activeCategory}
                  onSelect={setActiveCategory}
                  onAfterDelete={() => {
                    if (!currentCreator) return;
                    dispatch(fetchScriptCategoriesByCreator(currentCreator.id!));
                    lastRequestedPageRef.current = 1;
                    dispatch(fetchScriptsByFilter({ creatorId: currentCreator.id!, filterOrCategoryId: "all", page: 1 }));
                    if (activeCategory && activeCategory !== "all") {
                      dispatch(fetchScriptsByFilter({ creatorId: currentCreator.id!, filterOrCategoryId: activeCategory, page: 1 }));
                    }
                  }}
                />
                <ScriptList
                  items={items}
                  loading={scriptsLoading === REQUEST_LOADING_STATES.PENDING && items.length === 0}
                  listRef={listRef}
                  sentinelRef={sentinelRef}
                />
                {scriptsLoading === REQUEST_LOADING_STATES.PENDING && items.length > 0 && (
                  <div className="loader-script-wrapper">
                    <Loader />
                  </div>
                )}
              </section>
            ) : (
              <div className="no-category-card" role="note" aria-live="polite">
                {t("no-script-created")}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default ChatScriptsView;
